// uploadQueueSlice.js - Simplified Redux slice for upload queue
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { createMiddleware } from "../middlewareUtil";
import {
  uploadFileWorker,
  startUploadWorkers,
  cancelWorkerUpload,
} from "./workers";
import {
  findHighestChannelNumber,
  processFilesForQueue,
  sortFilesAndAssignChannels,
  isUploadQueueValid,
  MAX_WORKERS,
  FILE_CHUNK_SIZE,
} from "./utils";
import { uploadQueueAPI } from "./api";

export const UPLOAD_STATES = {
  IDLE: "idle",
  UPLOADING: "uploading",
  COMPLETE: "complete",
  ERROR: "error",
  CANCELED: "canceled",
  CANCELING: "canceling",
};

export const FILE_STATUS = {
  WAITING: "waiting",
  HASHING: "hashing",
  UPLOADING: "uploading",
  COMPLETE: "complete",
  ERROR: "error",
};

// Initial state
const initialState = {
  files: [],
  fileData: {
    fileType: "",
    sampleRate: 0,
    bitsPerSample: 0,
    float: false,
    numberOfChannels: 0,
    display: "unknown",
  },
  comment: "",

  uploadState: UPLOAD_STATES.IDLE,
  uploadFinishedAt: null,
  isUploading: false,
  caseId: null,
  clipId: null,
  description: null,
  progress: {}, // fileId -> { status, percent, message, error }

  config: {
    maxWorkers: MAX_WORKERS,
    chunkSize: FILE_CHUNK_SIZE,
  },

  isValid: false,
  hasError: false,
};

export const addFiles = createAsyncThunk(
  "uploadQueue/addFiles",
  async (fileList, { getState, rejectWithValue }) => {
    const state = getState().uploadQueue;

    try {
      const result = await processFilesForQueue(
        fileList,
        state.files,
        state.fileData,
      );

      if (!result.success) {
        return rejectWithValue({
          errors: result.errors,
        });
      }

      return result;
    } catch (error) {
      console.error("[uploadQueue/addFiles] Error processing files:", error);
      return rejectWithValue({
        errors: ["Error processing files: " + error.message],
      });
    }
  },
);

export const startUpload = createAsyncThunk(
  "uploadQueue/startUpload",
  async (_, { dispatch, getState }) => {
    console.log("[uploadQueue/startUpload] Starting upload process");

    const state = getState();
    const { uploadQueue } = state;
    const accessToken = state.auth.accessCode;
    const { id: clipId, caseId, description } = state.clip.clip;

    if (!uploadQueue.isValid || uploadQueue.files.length === 0) {
      throw new Error("Cannot start upload: invalid state or no files");
    }

    await uploadQueueAPI.start(accessToken, caseId, clipId, description);
    const result = await dispatch(startUploadWorkers());

    return {
      totalFiles: uploadQueue.files.length,
      startedWorkers: result.payload.startedWorkers,
      clipId,
      caseId,
      description,
    };
  },
);

export const cancelUpload = createAsyncThunk(
  "uploadQueue/cancelUpload",
  async ({ caseId, clipId }, { getState }) => {
    const state = getState();
    const accessToken = state.auth.accessCode;
    console.log(
      "[uploadQueue/cancelUpload] Cancelling upload for caseId:",
      caseId,
      "clipId:",
      clipId,
    );
    if (!caseId || !clipId) {
      throw new Error("Cannot cancel upload: missing caseId or clipId");
    }
    await uploadQueueAPI.cancel(accessToken, caseId, clipId);
    cancelWorkerUpload();
    return {};
  },
);

export const cancelCurrentUpload = () => (dispatch, getState) => {
  const state = getState();
  const { caseId, clipId } = state.uploadQueue;

  if (!caseId || !clipId) {
    throw new Error("Cannot cancel upload: missing caseId or clipId");
  }

  return dispatch(cancelUpload({ caseId, clipId }));
};

// Create the slice
const uploadQueueSlice = createSlice({
  name: "uploadQueue",
  initialState,
  reducers: {
    reset: () => cloneDeep(initialState),

    updateComment: (state, action) => {
      state.comment = action.payload;
    },

    updateConfig: (state, action) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };
    },

    removeFile: (state, action) => {
      const fileToRemove = action.payload;
      console.log(
        `[uploadQueue/removeFile] Removing file: ${fileToRemove.name}`,
        state.files,
      );

      const newList = state.files.filter(
        (file) => file.path !== fileToRemove.path,
      );
      if (newList.length === 0) {
        return cloneDeep(initialState);
      }

      delete state.progress[fileToRemove.id];
      state.files = sortFilesAndAssignChannels(newList);
      state.fileData.numberOfChannels = state.files.reduce(
        (acc, { fileData }) => acc + fileData.channels,
        0,
      );
      state.fileData.foundChannels = findHighestChannelNumber(state.files);
      state.isValid = isUploadQueueValid(state.fileData, state.files.length);
    },

    updateFileProgress: (state, action) => {
      const {
        fileId,
        status,
        percent,
        message,
        error,
        currentChunk,
        totalChunks,
      } = action.payload;

      state.progress[fileId] = {
        status,
        percent: percent || 0,
        message: message || "",
        error: error || null,
        currentChunk,
        totalChunks,
        updatedAt: Date.now(),
      };

      updateOverallState(state);
    },

    updateFileData: (state, action) => {
      state.fileData = {
        ...state.fileData,
        ...action.payload,
      };

      state.isValid = isUploadQueueValid(state.fileData, state.files.length);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(addFiles.pending, (_state) => {})
      .addCase(addFiles.fulfilled, (state, action) => {
        const { fileData, newFiles } = action.payload;

        state.files.push(...newFiles);
        state.files = sortFilesAndAssignChannels(state.files);
        state.fileData = fileData;

        state.isValid = isUploadQueueValid(state.fileData, state.files.length);
      })
      .addCase(addFiles.rejected, (_state, action) => {
        console.error("Failed to add files:", action.payload?.errors);
      })

      .addCase(startUpload.pending, (state) => {
        state.uploadState = UPLOAD_STATES.UPLOADING;
        state.isUploading = true;
        state.hasError = false;

        state.files.forEach((file) => {
          state.progress[file.id] = {
            status: FILE_STATUS.WAITING,
            percent: 0,
            message: "Waiting to start...",
            error: null,
          };
        });
      })
      .addCase(startUpload.fulfilled, (state, action) => {
        console.log("Upload started successfully:", action.payload);
        state.clipId = action.payload.clipId;
        state.caseId = action.payload.caseId;
        state.description = action.payload.description;
      })
      .addCase(startUpload.rejected, (state, action) => {
        console.error("Failed to start upload:", action.error);
        state.uploadState = UPLOAD_STATES.ERROR;
        state.isUploading = false;
        state.hasError = true;
      })
      .addCase(cancelUpload.pending, (state) => {
        console.log("[uploadQueue/cancelUpload] Cancelling upload");
        state.uploadState = UPLOAD_STATES.CANCELING;
      })
      .addCase(cancelUpload.fulfilled, (state) => {
        const initialStateClone = cloneDeep(initialState);
        initialStateClone.uploadState = UPLOAD_STATES.CANCELED;
        state = initialStateClone;
      })
      .addCase(cancelUpload.rejected, () => {})

      .addCase(uploadFileWorker.pending, () => {
        // Worker started - progress updates will come through updateFileProgress
      })
      .addCase(uploadFileWorker.fulfilled, (state, action) => {
        const { fileId } = action.payload;
        console.log(`File upload completed: ${fileId}`);

        updateOverallState(state);
      })
      .addCase(uploadFileWorker.rejected, (state, action) => {
        const { fileId, error } = action.payload;
        console.error(`File upload failed: ${fileId}`, error);

        updateOverallState(state);
      });
  },
});

function updateOverallState(state) {
  const progresses = Object.values(state.progress);

  if (progresses.length === 0) {
    return;
  }

  const hasErrors = progresses.some((p) => p.status === FILE_STATUS.ERROR);
  const allComplete = progresses.every(
    (p) => p.status === FILE_STATUS.COMPLETE,
  );
  const anyUploading = progresses.some(
    (p) =>
      p.status === FILE_STATUS.UPLOADING || p.status === FILE_STATUS.HASHING,
  );

  if (hasErrors) {
    state.uploadState = UPLOAD_STATES.ERROR;
    state.isUploading = false;
    state.hasError = true;
  } else if (allComplete) {
    state.uploadState = UPLOAD_STATES.COMPLETE;
    state.isUploading = false;
    state.hasError = false;
    state.uploadFinishedAt = Date.now();
  } else if (anyUploading) {
    state.uploadState = UPLOAD_STATES.UPLOADING;
    state.isUploading = true;
    state.hasError = false;
  }
}

export const uploadQueueMiddleware = createMiddleware([
  "uploadQueue/startUpload/pending",
]);

// Export actions
export const {
  reset,
  updateComment,
  updateConfig,
  removeFile,
  updateFileProgress,
  updateFileData,
} = uploadQueueSlice.actions;

// Export the workers for use in hooks
export { uploadFileWorker, startUploadWorkers };

// Export reducer
export default uploadQueueSlice.reducer;
